import React from 'react';
import { Sparkles } from 'lucide-react';
import { useTypewriter } from './useTypewriter';

/** Minimal inline formatter: only **bold** is supported inside knowledge-base answers. */
function renderInline(line, keyPrefix) {
  return line.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-${i}`} className="font-semibold text-gray-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
  });
}

function renderBody(text) {
  return String(text)
    .split('\n')
    .map((line, i) => {
      if (!line.trim()) return <div key={`sp-${i}`} className="h-2" />;
      if (line.trimStart().startsWith('•')) {
        return (
          <div key={`li-${i}`} className="flex gap-2 pl-0.5">
            <span aria-hidden="true" className="text-blue-600">
              •
            </span>
            <span>{renderInline(line.trimStart().slice(1).trim(), `li-${i}`)}</span>
          </div>
        );
      }
      return <div key={`p-${i}`}>{renderInline(line, `p-${i}`)}</div>;
    });
}

const ChatMessage = ({ message, streaming = false, onStreamTick, onStreamDone }) => {
  const isUser = message.role === 'user';
  const shown = useTypewriter(message.text, streaming && !isUser, onStreamDone);

  React.useEffect(() => {
    if (streaming) onStreamTick?.();
  }, [shown, streaming, onStreamTick]);

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-br from-blue-600 to-teal-600 px-3.5 py-2.5 text-[14px] leading-relaxed text-white shadow-sm">
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-teal-600 text-white">
        <Sparkles className="h-3.5 w-3.5" />
      </div>
      <div className="max-w-[85%] space-y-0.5 rounded-2xl rounded-bl-md border border-gray-100 bg-white px-3.5 py-2.5 text-[14px] leading-relaxed text-gray-700 shadow-sm">
        {renderBody(streaming ? shown : message.text)}
        {message.answeredBy === 'ai' && (
          <p className="pt-1.5 text-[10.5px] font-medium uppercase tracking-wide text-gray-400">
            AI generated · double-check anything important
          </p>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
